import React, { useState } from "react";
import Card from "./Card";
import { FOCUS_RING } from "./socUi";
import { formatAge } from "./socFreshness";
import { severityLabel } from "./socQueue";
import { latestUrgent, postureCounts, postureGauges } from "./socConsole";

const CHIPS = [
  ["action", "need action", "bg-status-error-bg text-status-error"],
  ["review", "to review", "bg-status-warning-bg text-status-warning"],
  ["stale", "stale", "bg-status-warning-bg text-status-warning outline-dashed outline-1 outline-[var(--status-warning)]"],
  ["healthy", "healthy", "bg-status-success-bg text-status-success"],
  ["notConnected", "not connected", "bg-theme-secondary text-content-muted"],
];

// Posture ribbon (/soc): one chip per posture with its area count (chips with no areas are left out);
// a chip jumps to the area tiles and highlights those areas. On the right, the newest open critical
// (else high) item, which opens the queue.
export const SocPostureRibbon = ({ tiles, now, rows, onJump, onUrgent }) => {
  const counts = postureCounts(tiles, now);
  const urgent = latestUrgent(rows);
  return (
    <section aria-label="Posture" data-testid="soc-posture"
      className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-border bg-theme-card-solid px-3 py-2.5 shadow-sm">
      <span className="mr-1 text-[11px] font-bold uppercase tracking-wider text-content-muted">Posture</span>
      {CHIPS.filter(([key]) => counts[key] > 0).map(([key, label, cls]) => (
        <button
          key={key}
          type="button"
          onClick={() => onJump(key)}
          data-testid={`soc-posture-${key}`}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[13px] font-semibold transition-transform motion-safe:hover:-translate-y-px ${cls} ${FOCUS_RING}`}
        >
          <span className="h-2 w-2 rounded-full bg-current" aria-hidden="true" />
          <span className="tabular-nums">{counts[key]}</span> {label}
        </button>
      ))}
      <span className="flex-1" />
      {urgent && (
        <button
          type="button"
          onClick={() => onUrgent(urgent)}
          data-testid="soc-posture-urgent"
          title="Open in the incident queue"
          className={`flex min-w-0 max-w-full items-center gap-2 rounded-md px-2 py-1 text-left text-[13px] text-content-secondary hover:bg-theme-hover ${FOCUS_RING}`}
        >
          <span className={`shrink-0 font-bold ${urgent.severity === "critical" ? "text-status-error" : "text-status-warning"}`}>
            ● {severityLabel(urgent.severity)}
          </span>
          <span className="truncate text-content-primary">{urgent.title}</span>
          {urgent.ageMs != null && <span className="shrink-0 text-content-muted">· {formatAge(urgent.ageMs)}</span>}
        </button>
      )}
    </section>
  );
};

// A half-circle gauge: the arc fills to `pct` of the target; green when the target is met, amber when
// at least half way, rose below that.
// The colour a gauge's arc, readout and focus ring share.
const gaugeColor = (g) => (g.met ? "var(--soc-v-green)" : g.pct >= 50 ? "var(--soc-v-amber)" : "var(--soc-v-rose)");

// `active`: this gauge is pointed at or focused (lifted, thicker arc); `dimmed`: another one is.
const Gauge = ({ g, active, dimmed, onEnter, onLeave }) => {
  const r = 40;
  const len = Math.PI * r;
  const color = gaugeColor(g);
  const width = active ? 12 : 10;
  return (
    <div
      tabIndex={0}
      onMouseEnter={onEnter}
      onFocus={onEnter}
      onBlur={onLeave}
      data-testid={`soc-gauge-${g.key}`}
      aria-label={`${g.title}: ${g.label}, ${g.detail}. ${g.gap}.`}
      className={`flex min-w-0 cursor-default flex-col items-center rounded-lg px-1 pb-1.5 pt-1 text-center transition-[transform,opacity,box-shadow,background-color] duration-150 focus:outline-none ${
        active ? "bg-theme-hover shadow-md motion-safe:-translate-y-0.5" : ""
      } ${dimmed ? "opacity-40" : ""}`}
    >
      <svg viewBox="-2 -2 104 60" className="w-full max-w-[8.5rem]" aria-hidden="true">
        <path d="M10 50 A40 40 0 0 1 90 50" fill="none" stroke="var(--chart-grid)" strokeWidth={width} strokeLinecap="round"
          className="transition-[stroke-width] duration-150" />
        <path d="M10 50 A40 40 0 0 1 90 50" fill="none" stroke={color} strokeWidth={width} strokeLinecap="round"
          strokeDasharray={`${((g.pct / 100) * len).toFixed(1)} ${len.toFixed(1)}`}
          className="transition-[stroke-width] duration-150"
          style={active ? { filter: "drop-shadow(0 1px 3px rgba(15, 23, 42, 0.3))" } : undefined} />
        <text x="50" y="48" textAnchor="middle" fontSize="17" fontWeight="800" fill="var(--text-primary)">{g.label}</text>
      </svg>
      <p className="text-xs font-semibold text-content-primary">{g.title}</p>
      <p className="text-[11px] text-content-muted">{g.detail}</p>
    </div>
  );
};

// Posture against Axaris targets: MFA, email settings, SIEM rule baselines. Each gauge appears only
// when its figure exists; with none the card is left out.
// Pointing at (or focusing) a gauge lifts it, fades the others and says under the gauges how far its
// figure is from the target.
export const SocPostureGauges = ({ tiles }) => {
  const [active, setActive] = useState(null); // gauge key | null
  const gauges = postureGauges(tiles);
  if (gauges.length === 0) {
    // No figure yet: the three gauges' places, greyed, so the row keeps its shape.
    return (
      <div data-testid="soc-chart-gauges" className="h-full">
        <Card className="h-full">
          <h3 className="font-semibold text-content-primary">Posture against targets</h3>
          <p className="text-xs text-content-muted">Axaris targets and baselines, not industry standards</p>
          <div className="mt-4 grid grid-cols-3 gap-3" aria-hidden="true">
            {["MFA adoption", "Email settings", "SIEM rules"].map((title) => (
              <div key={title} className="flex min-w-0 flex-col items-center text-center opacity-60">
                <svg viewBox="-2 -2 104 60" className="w-full max-w-[8.5rem]">
                  <path d="M10 50 A40 40 0 0 1 90 50" fill="none" stroke="var(--chart-grid)" strokeWidth="10" strokeLinecap="round" strokeDasharray="4 4" />
                  <text x="50" y="48" textAnchor="middle" fontSize="17" fontWeight="800" fill="var(--text-muted)">—</text>
                </svg>
                <p className="text-xs font-semibold text-content-muted">{title}</p>
              </div>
            ))}
          </div>
          <p className="mt-3 text-center text-xs text-content-secondary" data-testid="soc-gauges-empty">
            No MFA, email-settings or SIEM-rule figures yet. They appear once those sources are connected.
          </p>
        </Card>
      </div>
    );
  }
  const on = gauges.find((g) => g.key === active) || null;
  return (
    <div data-testid="soc-chart-gauges" className="h-full">
      <Card className="h-full">
        <h3 className="font-semibold text-content-primary">Posture against targets</h3>
        <p className="text-xs text-content-muted">Axaris targets and baselines, not industry standards</p>
        <div
          className={`mt-4 grid gap-3 ${gauges.length >= 3 ? "grid-cols-2 sm:grid-cols-3" : "grid-cols-2"}`}
          onMouseLeave={() => setActive(null)}
        >
          {gauges.map((g) => (
            <Gauge
              key={g.key}
              g={g}
              active={g.key === active}
              dimmed={!!active && g.key !== active}
              onEnter={() => setActive(g.key)}
              onLeave={() => setActive(null)}
            />
          ))}
        </div>
        <p className="mt-3 min-h-[1.25rem] text-center text-xs" data-testid="soc-gauges-readout" aria-live="polite">
          {on ? (
            <>
              <span className="font-semibold text-content-primary">{`${on.title} · ${on.label}`}</span>
              <span className="font-semibold" style={{ color: gaugeColor(on) }}>{` · ${on.gap}`}</span>
            </>
          ) : (
            <span className="text-content-muted">Point at a gauge to see how far it is from its target</span>
          )}
        </p>
      </Card>
    </div>
  );
};

// Tile fill for a tactic count: grey at 0, then amber to deep rose as the count grows.
const tacticColor = (count) =>
  count >= 5 ? "#be123c" : count >= 3 ? "var(--soc-v-rose)" : count >= 2 ? "var(--soc-v-orange)" : count >= 1 ? "var(--soc-v-amber)" : null;

const TACTIC_HINT = {
  Other: "Defender categories that are not ATT&CK tactics (for example Malware or Suspicious activity)",
  NotCategorised: "Synced before Axaris stored Defender's category; filled in on the next Defender sync",
};

// What an attacker is trying to do at each stage, in plain words (the hover readout).
const TACTIC_GOAL = {
  Reconnaissance: "Gathering information to plan an attack",
  ResourceDevelopment: "Setting up infrastructure, accounts or tools for an attack",
  InitialAccess: "Getting into your network",
  Execution: "Running malicious code",
  Persistence: "Keeping a foothold that survives restarts and password changes",
  PrivilegeEscalation: "Gaining higher permissions",
  DefenseEvasion: "Avoiding detection",
  CredentialAccess: "Stealing account names and passwords",
  Discovery: "Learning how your environment is laid out",
  LateralMovement: "Moving from one system to another",
  Collection: "Gathering the data they are after",
  CommandAndControl: "Talking to compromised systems to control them",
  Exfiltration: "Taking data out of your environment",
  Impact: "Disrupting, damaging or encrypting systems and data",
  Other: "Defender categories that are not ATT&CK tactics, such as Malware",
  NotCategorised: "Synced before the category was stored; filled in on the next Defender sync",
};

// Open Defender XDR incidents and alerts per MITRE ATT&CK tactic (summary.tactics), in matrix order.
// Pointing at (or focusing) a tile lifts it, fades the others and says under the tiles how many are
// open, their share and what that tactic means. Left out when the summary carries none (Defender XDR
// not connected).
// The 14 ATT&CK enterprise tactics in matrix order: the blank tiles when the summary has none.
const ATTACK_TACTICS = [
  ["Reconnaissance", "Reconnaissance"], ["ResourceDevelopment", "Resource Development"], ["InitialAccess", "Initial Access"],
  ["Execution", "Execution"], ["Persistence", "Persistence"], ["PrivilegeEscalation", "Privilege Escalation"],
  ["DefenseEvasion", "Defense Evasion"], ["CredentialAccess", "Credential Access"], ["Discovery", "Discovery"],
  ["LateralMovement", "Lateral Movement"], ["Collection", "Collection"], ["CommandAndControl", "Command and Control"],
  ["Exfiltration", "Exfiltration"], ["Impact", "Impact"],
];

export const SocMitreTactics = ({ tactics, emptyText = "No tactic data yet." }) => {
  const [activeKey, setActiveKey] = useState(null);
  if (!tactics || tactics.length === 0) {
    // Blank tiles: the matrix's shape without counts, which would read as "none found".
    return (
      <div data-testid="soc-chart-mitre" className="h-full">
        <Card className="h-full">
          <h3 className="font-semibold text-content-primary">MITRE ATT&amp;CK tactics</h3>
          <p className="text-xs text-content-muted">Open Defender XDR incidents and alerts by tactic</p>
          <div className="mt-3 grid grid-cols-2 gap-1.5 opacity-60 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8" aria-hidden="true">
            {ATTACK_TACTICS.map(([key, label]) => (
              <div key={key} className="flex min-h-[4rem] flex-col justify-between rounded-lg border border-dashed border-border p-2 text-content-muted">
                <span className="text-[11px] font-semibold leading-tight">{label}</span>
                <span className="text-xl font-extrabold">—</span>
              </div>
            ))}
          </div>
          <p className="mt-3 text-center text-xs text-content-secondary" data-testid="soc-mitre-empty">{emptyText}</p>
        </Card>
      </div>
    );
  }
  const total = tactics.reduce((s, t) => s + (t.count || 0), 0);
  const on = tactics.find((t) => t.key === activeKey) || null;
  return (
    <div data-testid="soc-chart-mitre" className="h-full">
      <Card className="h-full">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="font-semibold text-content-primary">MITRE ATT&amp;CK tactics</h3>
            <p className="text-xs text-content-muted">Open Defender XDR incidents and alerts by tactic</p>
          </div>
          <span className="text-xs text-content-muted tabular-nums">{total} open</span>
        </div>
        <div
          className="mt-3 grid grid-cols-2 gap-1.5 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8"
          onMouseLeave={() => setActiveKey(null)}
        >
          {tactics.map((t) => {
            const fill = tacticColor(t.count);
            const lit = t.key === activeKey;
            return (
              <div
                key={t.key}
                tabIndex={0}
                onMouseEnter={() => setActiveKey(t.key)}
                onFocus={() => setActiveKey(t.key)}
                onBlur={() => setActiveKey(null)}
                data-testid={`soc-tactic-${t.key}`}
                title={TACTIC_HINT[t.key] || `${t.label}: ${t.count} open`}
                aria-label={`${t.label}: ${t.count} open. ${TACTIC_GOAL[t.key] || ""}`}
                className={`relative flex min-h-[4rem] cursor-default flex-col justify-between rounded-lg p-2 transition-[transform,opacity,box-shadow] duration-150 focus:outline-none ${
                  fill ? "text-white" : "bg-theme-secondary text-content-muted"
                } ${lit ? "z-10 shadow-lg ring-2 ring-accent-cyan motion-safe:-translate-y-0.5 motion-safe:scale-105" : ""} ${
                  activeKey && !lit ? "opacity-40" : ""
                }`}
                style={fill ? { background: fill } : undefined}
              >
                <span className="text-[11px] font-semibold leading-tight">{t.label}</span>
                <span className="text-xl font-extrabold tabular-nums">{t.count}</span>
              </div>
            );
          })}
        </div>
        <p className="mt-3 min-h-[1.25rem] text-xs" data-testid="soc-mitre-readout" aria-live="polite">
          {on ? (
            <>
              <span className="font-semibold text-content-primary">{on.label}</span>
              <span className="text-content-secondary">
                {` · ${on.count} open${total > 0 ? ` · ${Math.round((on.count / total) * 100)}% of ${total}` : ""}`}
              </span>
              {TACTIC_GOAL[on.key] && <span className="text-content-muted">{` · ${TACTIC_GOAL[on.key]}`}</span>}
            </>
          ) : (
            <span className="text-content-muted">Point at a tactic to see what it means and how many are open</span>
          )}
        </p>
      </Card>
    </div>
  );
};
